
import {useState,useEffect, useMemo} from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CardHeaderWithActions, MwInputText, MwSelector, MwMultiSelector, MwTextArea,MwInputFile, MwButton, MwSpinnerButton, AlertMsg, MwFormSection } from '../components/ui'
import { MdInfoOutline, MdPeopleOutline, MdSubject, MdAttachFile } from 'react-icons/md';
import { FORM_INPUT_CLASS, FORM_LABEL_CLASS } from '../constants/formStyles';
import { getAllowedProducts, getDefaultStatusId, getLocalizedOptions, getPriorityOptions, normalizeSupportSettings } from '../constants/ticket';
import { useCurrent, useFetch, usePost } from '../hooks';
import { useTranslation } from 'react-i18next';
import { apiClient as axios } from '../utils/apiClient';
import { getAttachmentInfo, toAttachmentNamesPayload, uploadSupportFiles } from '../utils/attachments';
import { FaTimes } from 'react-icons/fa';
import { buildAssigneesPayload } from '../utils/ticketAssignees';
import { projectOptionLabel } from '../utils/projectHelpers';
import LangSwitch, { withLangLabel } from '../components/ui/LangSwitch';
import { validateBilingual } from '../utils/localized';

const stripHtmlText = (value) =>
    String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const TicketCreate = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const [t] = useTranslation('global')
    const {user,currentBranchId,currentCompanyId,currentTenantId,currentLangId} = useCurrent();
    const {postData:generateTicketNumber} = usePost();
    const {data:resultSubmit,loading:loadingSubmit,postData:submitTicket} = usePost(false);
    const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
    const { data: statusesResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/statuses/admin`);
    const { data: tenantsResult, loading: loadingTenants } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/getAll`);
    const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';
    const canAssignTickets =
        user?.role === 'admin' ||
        (isTruthyFlag(user?.supportAdminEnabled) &&
            (isTruthyFlag(user?.supportCanAssignTickets) || isTruthyFlag(user?.supportSuperAdminEnabled)));
    const canAssignProjectManager =
        user?.role === 'admin' ||
        (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportCanAssignProjectManager));
    const canCreateInternalTicket = user?.role === 'admin' || (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportCanViewInternalTickets));
    const canUseInternalProducts =
        user?.role === 'admin' ||
        (isTruthyFlag(user?.supportAdminEnabled) &&
            (isTruthyFlag(user?.supportCanViewInternalTickets) || isTruthyFlag(user?.supportSuperAdminEnabled)));
    const isSupportAdmin = isTruthyFlag(user?.supportAdminEnabled);
    const selfAssignedToName =
        user?.name ||
        user?.fullName ||
        user?.username ||
        user?.email ||
        (user?._id || user?.id || user?.userId) ||
        null;
    const { data: adminsResult } = useFetch((canAssignTickets || canAssignProjectManager) ? `${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins` : null);
    const [fileLoading,setFileLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const fileInputId = 'support_ticket_create_attachment_admin';

    const [productSelected,setProductSelected] = useState();
    const [sectionSelected,setSectionSelected] = useState();
    const [prioritySelected,setPrioritySelected] = useState();
    const [tenantSelected,setTenantSelected] = useState();
    const [projectSelected, setProjectSelected] = useState(null);
    const [didApplyProjectQuery, setDidApplyProjectQuery] = useState(false);
    const { data: projectsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/options`);
    const [assigneesSelected, setAssigneesSelected] = useState([]);
    const [projectManagerSelected, setProjectManagerSelected] = useState(null);
    const [subject,setSubject] = useState('');
    const [content,setContent] = useState('');
    // النسخة الإنجليزية + اللغة الظاهرة في الحقول.
    const [subjectEn,setSubjectEn] = useState('');
    const [contentEn,setContentEn] = useState('');
    const [formLang,setFormLang] = useState(currentLangId === 'en' ? 'en' : 'ar');
    const [ticketVisibility, setTicketVisibility] = useState('internal');
    const [ticketCreatedAt, setTicketCreatedAt] = useState('');
    const [expectedAt, setExpectedAt] = useState('');
    const [attchment,setAttchment] = useState([]);
    // الاسم الأصلي لكل ملف مرفوع { [url]: name }.
    const [attachmentNameMap, setAttachmentNameMap] = useState({});
    const [errors,setErrors] = useState();
    const [submited,setSubmited] = useState(false);
    const [createdTicketNumber,setCreatedTicketNumber] = useState(null);
    const [reservedTicketNumber,setReservedTicketNumber] = useState(null);
    const [reservedUserSequence,setReservedUserSequence] = useState(null);
    const supportSettingsBase = normalizeSupportSettings(supportSettingsResult);
    const supportSettings = {
        ...supportSettingsBase,
        statuses: normalizeSupportSettings({ data: { statuses: statusesResult?.data || [] } }).statuses
    };
    const tenants = Array.isArray(tenantsResult?.data) ? tenantsResult.data : [];
    const getTenantLabel = (tenant) => tenant?.tenantName || tenant?.username || tenant?._id || '';
    const scopedTenants = useMemo(() => {
        if (user?.role === 'admin') return tenants;
        if (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportSuperAdminEnabled)) return tenants;
        if (!isTruthyFlag(user?.supportAdminEnabled) || !isTruthyFlag(user?.supportTenantAdminEnabled)) return tenants;

        const scope = String(user?.supportTenantScope || 'own');
        if (scope === 'all') return tenants;

        if (scope === 'selected') {
            const allowedIds = Array.isArray(user?.supportTenantIds) ? user.supportTenantIds.map(String) : [];
            return tenants.filter((tenant) => allowedIds.includes(String(tenant?._id)));
        }

        const ownTenantId = user?.tenant_id?._id || user?.tenant_id || null;
        if (!ownTenantId) return [];
        return tenants.filter((tenant) => String(tenant?._id) === String(ownTenantId));
    }, [isTruthyFlag, tenants, user]);

    const tenantOptions = scopedTenants.map((tenant) => ({
        label: getTenantLabel(tenant),
        value: tenant._id,
        lang: currentLangId
    }));
    const productOptions = getLocalizedOptions(
        getAllowedProducts(supportSettings.products, user?.supportProductIds, {
            fallbackToAll:
                user?.role === 'admin' ||
                (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportSuperAdminEnabled)),
            includeInternal: canUseInternalProducts,
            internalProductIds: user?.supportInternalProductIds,
        }),
        currentLangId
    );
    const sectionOptions = getLocalizedOptions(supportSettings.sections, currentLangId);
    const priorityOptions = getPriorityOptions(currentLangId);
    const visibilityOptions = useMemo(() => ([
        { label: currentLangId === 'ar' ? 'خارجي - مشاركة مع العميل و فريق العمل' : 'External - Shared with customer and team', value: 'external', lang: currentLangId },
        { label: currentLangId === 'ar' ? 'داخلي - داخل فريق العمل فقط' : 'Internal - Team only', value: 'internal', lang: currentLangId },
    ]), [currentLangId]);
    const selectedVisibility = useMemo(
        () => visibilityOptions.find((opt) => opt.value === ticketVisibility) || visibilityOptions[0],
        [ticketVisibility, visibilityOptions]
    );
    const selectedTenantRecord = scopedTenants.find((tenant) => tenant?._id === tenantSelected?.value);
    const selectedCompany = selectedTenantRecord?.companies?.[0];
    const selectedBranch = selectedCompany?.branches?.[0];
    const selectedTenantOwner = selectedTenantRecord?.ownerUser || null;
    const selectedTenantCustomerName =
        selectedTenantOwner?.name ||
        selectedTenantRecord?.contactPersonName ||
        getTenantLabel(selectedTenantRecord) ||
        null;
    const selectedTenantCustomerEmail =
        selectedTenantOwner?.email ||
        selectedCompany?.mainEmail ||
        null;
    const selectedTenantCustomerMobile =
        selectedTenantOwner?.mobile ||
        selectedTenantRecord?.contactPersonMobile ||
        selectedTenantRecord?.tenantPhone ||
        selectedCompany?.mainPhone ||
        null;

    useEffect(() => {
        if (tenantSelected?.value && !scopedTenants.some((tenant) => String(tenant?._id) === String(tenantSelected.value))) {
            setTenantSelected(undefined);
        }
    }, [scopedTenants, tenantSelected]);

    // المشروع اختياري: لو اتختار مشروع العميل بيتحدد تلقائياً، ولو العميل اتغيّر لعميل تاني المشروع بيتشال.
    const projects = useMemo(() => (Array.isArray(projectsResult?.data) ? projectsResult.data : []), [projectsResult]);
    const projectOptions = useMemo(() => [
        { label: currentLangId === 'ar' ? 'بدون مشروع' : 'No project', value: '', lang: currentLangId },
        ...projects
            .filter((project) => !tenantSelected?.value || String(project.tenantId) === String(tenantSelected.value))
            .map((project) => ({ label: projectOptionLabel(project), value: project._id, lang: currentLangId }))
    ], [currentLangId, projects, tenantSelected?.value]);

    const selectProject = (option) => {
        const project = projects.find((item) => String(item._id) === String(option?.value || ''));
        if (!project) {
            setProjectSelected(null);
            return;
        }
        setProjectSelected({ label: projectOptionLabel(project), value: project._id, lang: currentLangId });
        const projectTenant = tenantOptions.find((item) => String(item.value) === String(project.tenantId));
        if (projectTenant) setTenantSelected(projectTenant);
        if (!sectionSelected?.value && project.sectionId) {
            const section = sectionOptions.find((item) => item.value === project.sectionId);
            if (section) setSectionSelected(section);
        }
        if (!productSelected?.value && project.ProductId) {
            const product = productOptions.find((item) => item.value === project.ProductId);
            if (product) setProductSelected(product);
        }
    };

    useEffect(() => {
        if (!projectSelected?.value) return;
        const project = projects.find((item) => String(item._id) === String(projectSelected.value));
        if (project && tenantSelected?.value && String(project.tenantId) !== String(tenantSelected.value)) {
            setProjectSelected(null);
        }
    }, [projects, projectSelected, tenantSelected]);

    const requestedProjectId = searchParams.get('projectId');
    const requestedSprintId = searchParams.get('sprintId');
    useEffect(() => {
        if (didApplyProjectQuery || !requestedProjectId || !projects.length || !tenantOptions.length) return;
        setDidApplyProjectQuery(true);
        selectProject({ value: requestedProjectId });
    }, [didApplyProjectQuery, requestedProjectId, projects.length, tenantOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const adminOptions = useMemo(() => {
        const admins = Array.isArray(adminsResult?.data) ? adminsResult.data : [];
        return [
            { label: currentLangId === 'ar' ? 'غير معين' : 'Unassigned', value: '', lang: currentLangId },
            ...admins.map((admin) => ({
                label: admin?.name || admin?.username || admin?._id,
                value: admin?._id,
                lang: currentLangId,
                email: admin?.email || '',
                subLabel: admin?.jobTitle || '',
                avatar: admin?.userPic || admin?.avatar || admin?.image || admin?.profileImage || admin?.photo || ''
            }))
        ];
    }, [adminsResult, currentLangId]);

    const uploadHandler = async (e) => {
        const files = Array.from(e.target.files || []);
        if(!files.length) return;
        setErrors((state) => ({ ...state, attachment: undefined }));
        setFileLoading(true);
        try {
            const uploaded = await uploadSupportFiles(files);
            setAttachmentNameMap((state) => ({ ...state, ...Object.fromEntries(uploaded.map((item) => [item.url, item.name])) }));
            setAttchment((state) => [...(Array.isArray(state) ? state : state ? [state] : []), ...uploaded.map((item) => item.url)]);
            e.target.value = '';
        } catch {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } finally {
            setFileLoading(false);
        }
    }

    const getAttachmentName = (url) => getAttachmentInfo(url, attachmentNameMap).name;

    const removeAttachment = async (attachmentUrl) => {
        if (!attachmentUrl) return;
        setDeleteLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${process.env.REACT_APP_UPLOADS_API_BASE_URL}/delete`,
                {
                    data: { keyFile: attachmentUrl },
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    validateStatus: () => true
                }
            );
            if (response?.status >= 200 && response?.status < 300) {
                setAttchment((state) => (Array.isArray(state) ? state : [state]).filter((url) => url !== attachmentUrl));
                setErrors((state) => ({ ...state, attachment: undefined }));
                const input = document.getElementById(fileInputId);
                if (input) {
                    input.value = '';
                }
                return;
            }
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } catch {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } finally {
            setDeleteLoading(false);
        }
    };

    const toIsoFromDatetimeLocal = (value) => {
        const raw = String(value || '').trim();
        if (!raw) return null;
        const [datePart, timePart] = raw.split('T');
        if (!datePart || !timePart) return null;
        const [y, m, d] = datePart.split('-').map((v) => Number(v));
        const [hh, mm] = timePart.split(':').map((v) => Number(v));
        if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
        const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
        if (Number.isNaN(dt.getTime())) return null;
        return dt.toISOString();
    };

    useEffect(() => {
        if (productSelected?.value && !productOptions.some((item) => item.value === productSelected.value)) {
            setProductSelected(undefined);
        }
    }, [productOptions, productSelected]);

    useEffect(() => {
        if (sectionSelected?.value && !sectionOptions.some((item) => item.value === sectionSelected.value)) {
            setSectionSelected(undefined);
        }
    }, [sectionOptions, sectionSelected]);

    useEffect(() => {
        const prepareTicketNumber = async () => {
            if (!user?._id || reservedTicketNumber) return;
            try {
                const ticketNumberResponse = await generateTicketNumber(
                    `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/number/next`,
                    { createdBy: user._id || null }
                );
                const generatedTicket = ticketNumberResponse?.data || {};
                if (generatedTicket?.ticketNumber) {
                    setReservedTicketNumber(generatedTicket.ticketNumber);
                    setReservedUserSequence(generatedTicket.userSequence || 1);
                }
            } catch {
                setReservedTicketNumber(null);
                setReservedUserSequence(null);
            }
        };

        prepareTicketNumber();
    }, [generateTicketNumber, reservedTicketNumber, user?._id]);



    const submitHandler = async () => {
        setErrors({});
        let _errors = {};
        const contentText = stripHtmlText(content);
        const expectedAtIso = toIsoFromDatetimeLocal(expectedAt);
        if(!sectionSelected?.value ){
            _errors.section = t('support.tickets.sectionRequired')
        }
        if(!tenantSelected?.value ){
            _errors.tenant = currentLangId === 'ar' ? 'اختيار العميل مطلوب' : 'Customer selection is required';
        }
        if(!productSelected?.value ){
            _errors.product = t('support.tickets.productRequired')
        }
        if(!prioritySelected?.value ){
            _errors.priority = t('support.tickets.priorityRequired')
        }
        // العنوان والمحتوى: لغة واحدة كاملة على الأقل، وأي لغة اتكتب فيها لازم تتكمل.
        const bilingual = validateBilingual([
            { key: 'subject', label: t('support.tickets.subject'), required: true, ar: subject, en: subjectEn },
            { key: 'content', label: t('support.tickets.content'), required: true, ar: content, en: contentEn },
        ], { isAr: currentLangId !== 'en', preferredLang: formLang });
        if (!bilingual.valid) {
            _errors.bilingual = bilingual;
            setFormLang(bilingual.firstLang);
        }
        if(!expectedAt){
            _errors.expectedAt = currentLangId === 'ar' ? 'الوقت المتوقع مطلوب' : 'Expected time is required';
        } else if (!expectedAtIso) {
            _errors.expectedAt = currentLangId === 'ar' ? 'الوقت المتوقع غير صالح' : 'Invalid expected time';
        }
        if(fileLoading){
            _errors.attachment = t('support.tickets.uploadInProgress')
        }
        if(Object.keys(_errors).length){
            setErrors(_errors);
            return;
        }

        try {
            const generatedTicket = reservedTicketNumber
                ? {
                    ticketNumber: reservedTicketNumber,
                    userSequence: reservedUserSequence || 1
                }
                : (await generateTicketNumber(
                    `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/number/next`,
                    {
                        createdBy: user._id || null
                    }
                ))?.data || {};
            if (!generatedTicket?.ticketNumber) {
                setErrors((state) => ({
                    ...state,
                    submit: currentLangId === 'ar' ? 'تعذر توليد رقم التذكرة' : 'Unable to generate ticket number'
                }));
                return;
            }

            const _data = 
                {
                    ticketNumber: generatedTicket.ticketNumber,
                    customerName: selectedTenantCustomerName,
                    customerId: selectedTenantOwner?._id || selectedTenantRecord?.userId || selectedTenantRecord?._id || null,
                    email : selectedTenantCustomerEmail || '',
                    mobile: selectedTenantCustomerMobile || '',
                    subject: subject.trim() || subjectEn.trim() || null,
                    content : contentText ? content : (contentEn || null),
                    subjectEn: subjectEn.trim(),
                    contentEn: stripHtmlText(contentEn) ? contentEn : '',
                    visibility: canCreateInternalTicket ? ticketVisibility : 'external',
                    sectionId : sectionSelected.value || null,
                    ProductId: productSelected.value || null,
                    priority: prioritySelected.value || 'medium',
                    attachments : attchment.length ? attchment : null,
                    attachmentNames: toAttachmentNamesPayload(attachmentNameMap, attchment),
                    statusId: getDefaultStatusId(supportSettings?.statuses),
                    createdBy: user._id || null,
                    userSequence: generatedTicket.userSequence || 1,
                    branchId: selectedBranch?._id || currentBranchId || null,
                    companyId: selectedCompany?._id || currentCompanyId || null,
                    tenantId: selectedTenantRecord?._id || currentTenantId || null,
                    tenantName: getTenantLabel(selectedTenantRecord),
                    ...(projectSelected?.value ? { projectId: projectSelected.value } : {}),
                    // إنشاء تذكرة من داخل دورة في صفحة المشروع.
                    ...(projectSelected?.value && requestedSprintId && String(projectSelected.value) === String(requestedProjectId) ? { sprintId: requestedSprintId } : {}),
                    ...(canCreateInternalTicket && ticketCreatedAt ? { createdAt: new Date(ticketCreatedAt).toISOString() } : {}),
                    expectedAt: expectedAtIso,
                    ...(canAssignTickets
                        ? buildAssigneesPayload(assigneesSelected)
                        : (isSupportAdmin && user?._id
                            ? buildAssigneesPayload([{ value: user._id, label: selfAssignedToName, email: user?.email || '' }])
                            : {})),
                    ...(canAssignProjectManager && projectManagerSelected?.value
                        ? {
                            projectManagerId: projectManagerSelected.value,
                            projectManagerName: projectManagerSelected.label,
                            projectManagerEmail: projectManagerSelected.email || ''
                        }
                        : {})
                }
            console.log(_data);
            await submitTicket(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}`,_data);
        } catch (error) {
            // رسالة السيرفر نفسها بدل رسالة عامة، عشان يبان فين المشكلة.
            setErrors((state) => ({
                ...state,
                submit: error?.response?.data?.message || (currentLangId === 'ar' ? 'فشل إنشاء التذكرة، حاول مرة أخرى' : 'Failed to create ticket, please try again')
            }));
        }
    }
    useEffect(() => {
        if(resultSubmit){
            console.log(resultSubmit)
            if (resultSubmit?.status !== 1) {
                setSubmited(false);
                setErrors((state) => ({
                    ...state,
                    submit: resultSubmit?.message || (currentLangId === 'ar' ? 'فشل إنشاء التذكرة، حاول مرة أخرى' : 'Failed to create ticket, please try again')
                }));
                return;
            }

            setCreatedTicketNumber(resultSubmit?.data?.ticketNumber || null);
            setSubmited(true);
            // Redirect after 2 seconds
            // لو جه من صفحة المشروع يرجع لها.
            const createdProjectId = resultSubmit?.data?.projectId;
            const backToProject = requestedProjectId && String(createdProjectId || '') === String(requestedProjectId);
            const timer = setTimeout(() => {
                navigate(backToProject ? `/${params.tenant}/projects/show/${createdProjectId}` : `/${params.tenant}/tickets`);
            }, 2000);
            return () => clearTimeout(timer);
        }
    },[currentLangId, navigate, params.tenant, requestedProjectId, resultSubmit]);

    return (
        <>
        {
            submited ? 
            <AlertMsg type={`Large`}>
                <span className='font-bold '>{t('support.tickets.successTitle')}</span>
                {createdTicketNumber && (
                    <span>{currentLangId === 'ar' ? ` رقم التذكرة: ${createdTicketNumber}` : ` Ticket #: ${createdTicketNumber}`}</span>
                )}
                <span> {t('support.tickets.successBody1')} </span>
                <span>{t('support.tickets.successBody2')}</span>
            </AlertMsg>:
            <div className="admin-page">
                <CardHeaderWithActions title={t('support.tickets.createTitle')}>
                    {loadingSubmit ? <MwSpinnerButton/> :<MwButton onClick={submitHandler} type='saveBtn' disabled={fileLoading || deleteLoading}>{t('support.tickets.send')}</MwButton>}
                </CardHeaderWithActions>
                <div className='admin-panel p-3 md:p-5 flex flex-col gap-4'>
                    {errors?.submit && (
                        <AlertMsg type="Error">{errors.submit}</AlertMsg>
                    )}
                    <MwFormSection
                        title={currentLangId === 'ar' ? 'بيانات التذكرة' : 'Ticket details'}
                        icon={<MdInfoOutline size={15} />}
                    >
                        <MwInputText
                            label={t('support.tickets.ticketNumber')}
                            labelClassName={FORM_LABEL_CLASS}
                            classNameInput={FORM_INPUT_CLASS}
                            value={reservedTicketNumber || ''}
                            readOnly
                            disabled
                            placeholder={currentLangId === 'ar' ? 'جاري توليد رقم التذكرة...' : 'Generating ticket number...'}
                        />
                        <MwSelector
                            className={` z-[61]`}
                            label={currentLangId === 'ar' ? 'العميل' : 'Customer'}
                            initalValue={currentLangId === 'ar' ? 'اختر عميل' : 'Select customer'}
                            dataType={'labelValue'}
                            _data={tenantOptions}
                            selectedItem={tenantSelected}
                            setSelectedItem={setTenantSelected}
                            withAddNew={false}
                            invalid={!!errors?.tenant}
                            invalidMsg={errors?.tenant}
                            />
                        <MwSelector
                            className={` z-[60]`}
                            label={currentLangId === 'ar' ? 'المشروع (اختياري)' : 'Project (optional)'}
                            initalValue={currentLangId === 'ar' ? 'بدون مشروع' : 'No project'}
                            dataType={'labelValue'}
                            _data={projectOptions}
                            selectedItem={projectSelected || projectOptions[0]}
                            setSelectedItem={selectProject}
                            withAddNew={false}
                            />
                        {canCreateInternalTicket && (
                            <MwSelector
                                className={` z-[57]`}
                                label={currentLangId === 'ar' ? 'نوع التذكرة' : 'Ticket Type'}
                                initalValue={currentLangId === 'ar' ? 'اختر النوع' : 'Select type'}
                                dataType={'labelValue'}
                                _data={visibilityOptions}
                                selectedItem={selectedVisibility}
                                setSelectedItem={(item) => setTicketVisibility(item?.value || 'internal')}
                                withAddNew={false}
                            />
                        )}
                        <MwSelector
                            className={` z-50`}
                            label={t('support.tickets.product')}
                            initalValue={t('support.tickets.selectProductPlaceholder')}
                            dataType={'labelValue'}
                            _data={productOptions}
                            selectedItem={productSelected}
                            setSelectedItem={setProductSelected}
                            withAddNew={false}
                            invalid={!!errors?.product}
                            invalidMsg={errors?.product}
                            />
                        <MwSelector
                            className={` z-[49]`}
                            label={t('support.tickets.section')}
                            initalValue={t('support.tickets.selectSectionPlaceholder')}
                            dataType={'labelValue'}
                            _data={sectionOptions}
                            selectedItem={sectionSelected}
                            setSelectedItem={setSectionSelected}
                            withAddNew={false}
                            invalid={!!errors?.section}
                            invalidMsg={errors?.section}
                            />
                        <MwSelector
                            className={` z-[48]`}
                            label={t('support.tickets.priority')}
                            initalValue={t('support.tickets.selectPriorityPlaceholder')}
                            dataType={'labelValue'}
                            _data={priorityOptions}
                            selectedItem={prioritySelected}
                            setSelectedItem={setPrioritySelected}
                            withAddNew={false}
                            invalid={!!errors?.priority}
                            invalidMsg={errors?.priority}
                            />
                    </MwFormSection>

                    <MwFormSection
                        title={currentLangId === 'ar' ? 'المواعيد والتعيين' : 'Dates & assignment'}
                        icon={<MdPeopleOutline size={15} />}
                    >
                        {canCreateInternalTicket && (
                            <MwInputText
                                label={currentLangId === 'ar' ? 'تاريخ التذكرة' : 'Ticket Date'}
                                labelClassName={FORM_LABEL_CLASS}
                                classNameInput={FORM_INPUT_CLASS}
                                inputType="datetime-local"
                                value={ticketCreatedAt}
                                onChange={(e) => setTicketCreatedAt(e.target.value)}
                            />
                        )}
                        <MwInputText
                            label={currentLangId === 'ar' ? 'الوقت المتوقع' : 'Expected time'}
                            labelClassName={FORM_LABEL_CLASS}
                            classNameInput={FORM_INPUT_CLASS}
                            inputType="datetime-local"
                            value={expectedAt}
                            onChange={(e) => setExpectedAt(e.target.value)}
                            invalid={!!errors?.expectedAt}
                            invalidMsg={errors?.expectedAt}
                        />
                        {canAssignProjectManager && (
                            <MwSelector
                                className={` z-[40]`}
                                label={currentLangId === 'ar' ? 'مدير المشروع' : 'Project Manager'}
                                initalValue={currentLangId === 'ar' ? 'اختر مدير المشروع...' : 'Select project manager...'}
                                dataType={'labelValue'}
                                _data={adminOptions}
                                selectedItem={projectManagerSelected}
                                setSelectedItem={setProjectManagerSelected}
                                withAddNew={false}
                                showAvatarFallback
                            />
                        )}
                        {canAssignTickets && (
                            <div className={canCreateInternalTicket && canAssignProjectManager ? '' : 'sm:col-span-2'}>
                                <MwMultiSelector
                                    className={` z-[39]`}
                                    label={currentLangId === 'ar' ? 'تعيين التذكرة لموظفي الدعم' : 'Assign Ticket to Support Admins'}
                                    initalValue={currentLangId === 'ar' ? 'غير معينة — اختر موظف أو أكثر...' : 'Unassigned — select one or more admins...'}
                                    _data={adminOptions}
                                    selectedItems={assigneesSelected}
                                    setSelectedItems={setAssigneesSelected}
                                />
                            </div>
                        )}
                    </MwFormSection>

                    <MwFormSection
                        title={currentLangId === 'ar' ? 'محتوى التذكرة' : 'Ticket content'}
                        icon={<MdSubject size={15} />}
                        cols={1}
                    >
                        <LangSwitch
                            value={formLang}
                            onChange={setFormLang}
                            isAr={currentLangId !== 'en'}
                            filled={{ ar: Boolean(subject.trim() || stripHtmlText(content)), en: Boolean(subjectEn.trim() || stripHtmlText(contentEn)) }}
                            errors={errors?.bilingual?.errors}
                            messages={errors?.bilingual?.messages}
                        />
                        {['ar', 'en'].map((code) => (
                            <div key={code} className={formLang === code ? 'flex flex-col gap-3' : 'hidden'}>
                                <MwInputText
                                    label ={withLangLabel(t('support.tickets.subject'), code, currentLangId !== 'en')}
                                    labelClassName={FORM_LABEL_CLASS}
                                    classNameInput={FORM_INPUT_CLASS}
                                    value = {code === 'ar' ? subject : subjectEn}
                                    onChange={(e) => (code === 'ar' ? setSubject : setSubjectEn)(e.target.value)}
                                    invalid={!!errors?.bilingual?.errors?.[code]?.subject}
                                    invalidMsg={errors?.bilingual?.errors?.[code]?.subject}
                                    />
                                <div>
                                    <MwTextArea
                                        label ={withLangLabel(t('support.tickets.content'), code, currentLangId !== 'en')}
                                        minHeight={480}
                                        value = {code === 'ar' ? content : contentEn}
                                        onChange={(e) => (code === 'ar' ? setContent : setContentEn)(e.target.value)}
                                        invalid={!!errors?.bilingual?.errors?.[code]?.content}
                                        invalidMsg={errors?.bilingual?.errors?.[code]?.content}
                                        dir={code === 'ar' ? 'rtl' : 'ltr'}
                                        rich
                                        />
                                </div>
                            </div>
                        ))}
                        <div className='rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-3 py-2'>
                            <div className='flex items-center gap-2 text-xs font-semibold text-slate-600'>
                                <MdAttachFile size={15} className='text-slate-400 shrink-0' />
                                <MwInputFile
                                    label={t('support.tickets.attachFile')}
                                    onChangeHandler={uploadHandler}
                                    acceptTypes='*/*'
                                    loading= {fileLoading}
                                    disabled={fileLoading || deleteLoading}
                                    id={fileInputId}
                                    classNameCustom='flex-1 !p-0'
                                    multiple
                                    />
                            </div>
                            {attchment.length > 0 && (
                                <div className='mt-2 flex flex-wrap gap-2'>
                                    {attchment.map((attachmentUrl) => (
                                        <div key={attachmentUrl} className="flex items-center gap-1.5 max-w-full rounded-full border border-slate-200 bg-white ps-3 pe-1 py-0.5">
                                            <a
                                                href={attachmentUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[11px] font-semibold text-slate-600 hover:text-[#08203f] truncate max-w-[220px]"
                                            >
                                                {getAttachmentName(attachmentUrl)}
                                            </a>
                                            <button
                                                type="button"
                                                onClick={() => removeAttachment(attachmentUrl)}
                                                disabled={fileLoading || deleteLoading}
                                                className="p-1 hover:bg-red-50 rounded-full text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                                            >
                                                <FaTimes size={11} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {errors?.attachment && <p className='mt-1 text-xs text-red-500'>{errors.attachment}</p>}
                            <p className='mt-1 text-[11px] text-slate-400'>
                                {currentLangId === 'ar'
                                    ? 'يمكنك رفع جميع الصيغ المدعومة بما فيها MP4 وبدون حد حجم من جهة الواجهة.'
                                    : 'You can upload supported formats including MP4 without a size limit from the client side.'}
                            </p>
                        </div>
                    </MwFormSection>
                    {loadingTenants && (
                        <p className='text-xs text-slate-500'>
                            {currentLangId === 'ar' ? 'جاري تحميل العملاء...' : 'Loading customers...'}
                        </p>
                    )}
                </div>
            </div>
        }
        </>
    )
}

export default TicketCreate

